import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OrgsService } from "@app2/account/orgs.service";
import { RestClient } from "@app2/clients/rest-client";
import { EnvironmentService } from "@app2/shared/services/environment.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { SlugName, TicketWatcherType } from "@app2/ticket/ticket-constants";
import { TicketFile } from "@app2/type-defs/ticket/ticket-files.types";
import {
    BulkCloseResponse,
    defaultTicketElements,
    HierarchicalTaskPageResponse,
    SlugIdVersion,
    Ticket,
    TicketEscalationSettings,
    TicketLink,
    TicketLog,
    TicketNote,
    TicketPageFilters,
    TicketPageRequest,
    TicketPagesResponse,
    TicketSearch,
    TicketSummary,
    TicketTemplate,
    TicketWatcher,
    WorkflowStats,
} from "@app2/type-defs/ticket/ticket-types";
import * as _ from "underscore";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

@Injectable({
    providedIn: "root",
})
export class TicketClientService {
    private ticketClient: RestClient;
    private ticketClientNoOrg: RestClient;

    constructor(httpClient: HttpClient,
                envService: EnvironmentService,
                orgsService: OrgsService,
                private notificationsService: NotificationsService) {
        const baseUrl = envService.getConfig().ticketService;

        this.ticketClient = new RestClient(baseUrl, httpClient, orgsService);
        this.ticketClientNoOrg = new RestClient(baseUrl, httpClient);
    }

    get linkTypes() {
        return [
            "DEPENDS_ON",
            "DEPENDED_ON_BY",
            "RELATED_TO",
            "CAUSED_BY",
            "CAUSES",
            "DUPLICATES",
            "DUPLICATED_BY",
            "BLOCKS",
            "BLOCKED_BY",
        ];
    }

    public static getBlankTicket(slugName: SlugName, ownerId: uuid): Ticket {
        return {
            slugName: slugName,
            ownedBy: ownerId,

            orgId: undefined,
            slugId: undefined,
            active: undefined,
            version: undefined,
            summary: undefined,
            description: undefined,
            state: undefined,
            autoGenerated: false,
            createdBy: undefined,
            createdOn: undefined,
            updatedBy: undefined,
            updatedOn: undefined,
            dueOn: undefined,
            timeZoneId: undefined,
            id: undefined,
            watchers: ownerId ? [{ personId: ownerId, type: "all" }] : [],
            searches: [],
            links: [],
            severity: "NONE",
            lineItemIds: [],
            riskControlIds: [],
            previousTaskSlugId: undefined,
            nextTaskSlugId: undefined,
            taskData: slugName === "task" ? {
                taskScheduleId: undefined,
                parentSlugId: undefined,
                summaryNote: undefined,
            } : undefined,
            tagIds: [],
            slaDueDate: undefined,
        };
    }

    public getOlderLogs(ticket: Ticket, fromLog: TicketLog, count: number): Promise<TicketLog> {
        const resource = "/workflow/" + ticket.slugName + "/ticket/" + ticket.slugId + "/logs/before";
        const params = count ? { num: count } : {};
        return this.ticketClient.doPost(resource, fromLog, params, ["query"]);
    }

    public createTicket(ticket: Ticket, orgId?: uuid): Promise<Ticket> {
        const resource = orgId
            ? `/org/${orgId}/workflow/${ticket.slugName}/ticket`
            : `/workflow/${ticket.slugName}/ticket`;
        const client = orgId ? this.ticketClientNoOrg : this.ticketClient;
        return client.doPost(resource, ticket, undefined, ["query", "states", "events"])
            .then(response => this.mapWorkflowData(response));
    }

    public updateTicket(ticket: Ticket, updates: Partial<Ticket>): Promise<Ticket> {
        const resource = "/workflow/" + ticket.slugName + "/ticket/" + ticket.slugId;
        return this.ticketClient.doPut(resource, updates, { ticketVersion: ticket.version },
            ["query", "states", "events"])
            .then(response => this.mapWorkflowData(response))
            .then(response => this.transformSearchQueriesFormat(response));
    }

    public addNote(ticket: Ticket, logId, noteText: string): Promise<any> {
        const resource = "/workflow/" + ticket.slugName + "/ticket/" + ticket.slugId + "/notes/";
        const params: TicketNote = {
            noteText: noteText,
        };
        if (logId) {
            params.ticketLogId = logId;
        }
        return this.ticketClient.doPost(resource, undefined, params);
    }

    public editNote(ticket: Ticket, noteId: uuid, noteText: string): Promise<void> {
        const resource = "/workflow/" + ticket.slugName + "/ticket/" + ticket.slugId + "/notes/" + noteId;
        const params: TicketNote = {
            noteText: noteText,
        };
        return this.ticketClient.doPost(resource, undefined, params);
    }

    public addLink(sourceSlugName, sourceSlugId, targetSlugName, targetSlugId, type): Promise<TicketLink> {
        const resource = "/workflow/" + sourceSlugName + "/ticket/" + sourceSlugId +
            "/link/workflow/" + targetSlugName + "/ticket/" + targetSlugId;
        const params = { linkType: type };
        return this.ticketClient.doPost(resource, undefined, params);
    }

    public deleteLink(slugName, slugId, targetSlugName, targetSlugId, type): Promise<TicketLink> {
        const resource = "/workflow/" + slugName + "/ticket/" + slugId +
            "/link/workflow/" + targetSlugName + "/ticket/" + targetSlugId;
        return this.ticketClient.doDelete(resource, { linkType: type });
    }

    public getTemplates(): Promise<TicketTemplate[]> {
        return this.ticketClient.doGet("/template");
    }

    public editTemplate(entity: TicketTemplate): Promise<TicketTemplate[]> {
        return this.ticketClient.doPut("/template", entity);
    }

    public deleteSearch(slugName, slugId, searchId) {
        const resource = "/workflow/" + slugName + "/ticket/" + slugId + "/search/" + searchId;
        return this.ticketClient.doDelete(resource);
    }

    public updateSearch(slugName, slugId, search) {
        const resource = "/workflow/" + slugName + "/ticket/" + slugId + "/search/" + search.id;
        return this.ticketClient.doPut(resource, search, undefined, ["query"]);
    }

    public downloadCsv(slugName, filters?: Partial<TicketPageFilters>, sort?: string,
                       descending?: boolean, pageStart?: TicketSummary) {
        const params = { sort, descending, filters, pageStart };
        const resource = "/workflow/" + slugName + "/filtered-page";
        return this.ticketClient.downloadCsvPost(resource, params);
    }

    public downloadCsvBySlugIds(slugName, slugIds) {
        const resource = "/workflow/" + slugName + "/tickets-csv";
        return this.ticketClient.downloadCsvGet(resource, undefined, { slugIds });
    }

    public getTicketPages(slugName: SlugName, pageRequest: Partial<TicketPageRequest>): Promise<TicketPagesResponse> {
        return this.ticketClient.doPost<TicketPagesResponse>("/workflow/" + slugName + "/filtered-pages", pageRequest, {});
    }

    public getTicketPage(slugName: SlugName, pageRequest: Partial<TicketPageRequest>, exclude: string[] = []): Promise<Ticket[]> {
        return this.ticketClient.doPost<Ticket[]>("/workflow/" + slugName + "/filtered-page",
            pageRequest, {}, ["query", ...exclude]);
    }

    public getInboxTicketPages(slugName: SlugName, pageRequest: Partial<TicketPageRequest>): Promise<TicketPagesResponse> {
        return this.ticketClientNoOrg.doPost<TicketPagesResponse>("/" + slugName + "/filtered-inbox-pages", pageRequest, {});
    }

    public getInboxTicketPage(slugName: SlugName, pageRequest: Partial<TicketPageRequest>): Promise<Ticket[]> {
        return this.ticketClientNoOrg.doPost<Ticket[]>("/" + slugName + "/filtered-inbox-page", pageRequest, {}, ["query"]);
    }

    public getTicketCounts(slugName: SlugName, filters: Partial<TicketPageFilters>, groupBy: string): Promise<Record<string, number>> {
        return this.ticketClient.doPost(`/workflow/${ slugName }/ticket/counts`, filters, { groupBy });
    }

    public downloadInboxCsv(slugName: SlugName, filters?: Partial<TicketPageFilters>, sort?: string,
                            descending?: boolean, pageStart?: TicketSummary): Promise<Blob> {
            const params = { sort, descending, filters, pageStart };
            return this.ticketClientNoOrg.downloadCsvPost(`/${slugName}/filtered-inbox-page`, params);
    }

    public downloadInboxCsvsBySlugIds(slugName: SlugName, slugIdsByOrg: Record<uuid, number[]>): Promise<Blob> {
        return this.ticketClientNoOrg.downloadCsvPost(`/${slugName}/ticket/by-slug-ids`, slugIdsByOrg);
    }

    public getHierarchicalTaskPage(pageRequest: Partial<TicketPageRequest>): Promise<HierarchicalTaskPageResponse> {
        const resource = `/workflow/task/hierarchical-page`;
        return this.ticketClient.doPost<HierarchicalTaskPageResponse>(resource, pageRequest);
    }

    public getHierarchicalInboxTaskPage(pageRequest: Partial<TicketPageRequest>): Promise<HierarchicalTaskPageResponse> {
        return this.ticketClientNoOrg.doPost("/task/hierarchical-page", pageRequest);
    }

    public getTicket(slugName: SlugName, slugId: number, includedElements: string[] = defaultTicketElements): Promise<Ticket> {
        const resource = `/workflow/${slugName}/ticket/${slugId}`;
        return this.ticketClient.doGet<Ticket>(resource, { includedElements }, ["query", "states", "events"])
            .then(this.mapWorkflowData);
    }

    public getTicketByOrg(slugName: SlugName, slugId: number, orgId: uuid, includedElements: string[] = defaultTicketElements): Promise<Ticket> {
        const resource = `/org/${ orgId }/workflow/${ slugName }/ticket/${ slugId }`;
        return this.ticketClientNoOrg.doGet<Ticket>(resource, { includedElements }, ["query", "states", "events"])
            .then(this.mapWorkflowData);
    }

    public claimTicket(orgId: uuid, slugId: number): Promise<Ticket> {
        const resource = `/org/${orgId}/workflow/incident/ticket/${slugId}/claim`;
        return this.ticketClientNoOrg.doPost(resource, null);
    }

    public escalateTicket(slugName: SlugName, slugId: number, settings: TicketEscalationSettings, ticketVersion: number): Promise<Ticket> {
        const resource = `/workflow/${slugName}/ticket/${slugId}/escalate`;
        return this.ticketClient.doPost(resource, settings, { ticketVersion });
    }

    public mergeTickets(slugName: SlugName, slugId: number, slugIdsToMerge: number[]): Promise<void> {
        return this.ticketClient.doPost<void>(`/workflow/${ slugName }/ticket/${ slugId }/merge-tickets`,
            undefined, { slugIds: slugIdsToMerge });
    }

    public bulkClose(slugName: SlugName, resolution: string, detail: string, ownedBy: uuid, ticketState: string,
                     slugs: SlugIdVersion[]): Promise<BulkCloseResponse> {
        const params = {
            resolution,
            detail,
            ownedBy,
            slugs,
            ticketState,
        };
        return this.ticketClient.doPut(`/workflow/${ slugName }/bulk-close`, params);
    }

    public bulkCloseBySearch(slugName: SlugName, resolution: string, detail: string, ownedBy: uuid, ticketState: string,
                             filters: TicketPageFilters): Promise<BulkCloseResponse> {
        const params = {
            resolution,
            detail,
            ownedBy,
            filters,
            ticketState,
        };
        return this.ticketClient.doPut(`/workflow/${ slugName }/bulk-close-search`, params);
    }

    public bulkCloseInbox(slugName: SlugName, resolution: string, detail: string, ownedBy: uuid, ticketState: string,
                          slugsByOrg: Record<uuid, SlugIdVersion[]>): Promise<BulkCloseResponse> {
        const params = {
            resolution,
            detail,
            ownedBy,
            slugsByOrg,
            ticketState,
        };
        return this.ticketClientNoOrg.doPut(`/${ slugName }/bulk-close`, params);
    }

    public bulkCloseInboxBySearch(slugName: SlugName, resolution: string, detail: string, ownedBy: uuid, ticketState: string,
                             filters: TicketPageFilters): Promise<BulkCloseResponse> {
        const params = {
            resolution,
            detail,
            ownedBy,
            filters,
            ticketState,
        };
        return this.ticketClientNoOrg.doPut(`/${ slugName }/bulk-close-search`, params);
    }

    public handleTicketUpdateError(error: any): void {
        if (error.status === 409) {
            this.notificationsService.showError("TICKET_VERSION_ERROR", error);
            return;
        }

        const message = getMessageForError(error.error, "ERROR_SAVING_TICKET");
        this.notificationsService.showError(message, error);
    }

    public getUrlForUpload(slugName: SlugName, slugId: number, contentType: string): Promise<TicketFile> {
        const resource = `/workflow/${slugName}/ticket/${slugId}/file?safariIgnoresCacheControlHeader=${Math.random()}`;
        return this.ticketClient.doGet<TicketFile>(resource, { contentType: contentType || "application/octet-stream" });
    }

    public updateFileMetadata(fileData: TicketFile, slugName: SlugName, slugId: number): Promise<TicketFile> {
        const resource = `/workflow/${slugName}/ticket/${slugId}/file`;
        return this.ticketClient.doPost(resource, fileData);
    }

    public deleteFile(fileId: string, slugName: SlugName, slugId: number) {
        const resource = "/workflow/" + slugName + "/ticket/" + slugId + "/file/" + fileId;
        return this.ticketClient.doDelete(resource);
    }

    public addSearch(slugName: SlugName, slugId: number, newSearch: Partial<TicketSearch>): Promise<TicketSearch> {
        const resource = `/workflow/${ slugName }/ticket/${ slugId }/search`;
        return this.ticketClient.doPost(resource, newSearch, {}, ["query"]);
    }

    public getWorkflowStatsSummary(slugName: SlugName, begin: date, end: date, closedStates: string[]): Promise<WorkflowStats> {
        const resource = `/workflow/${slugName}/stats/summary`;
        return this.ticketClient.doGet(resource, { begin, end, closedState: closedStates });
    }

    public addWatchers(slugName: SlugName, slugId: number, type: TicketWatcherType, personIds: uuid[]) {
        return this.ticketClient.doPost(`/workflow/${ slugName }/ticket/${ slugId }/watchers`,
            personIds, { type });
    }

    public bulkAddWatchers(ticket: Ticket, watcherList: TicketWatcher[]): Promise<TicketWatcher[]> {
        const resource = `/workflow/${ ticket.slugName }/ticket/${ ticket.slugId }/bulkWatchers`;
        return this.ticketClient.doPost(resource, watcherList);
    }

    public deleteWatcher(ticket: Ticket, watcherId: uuid): Promise<Ticket> {
        const resource = `/workflow/${ ticket.slugName }/ticket/${ ticket.slugId }/watcher/${ watcherId }`;
        return this.ticketClient.doDelete(resource);
    }

    public getTicketEscalationSettings(): Promise<TicketEscalationSettings> {
        return this.ticketClient.doGet(`/ticket-escalation-settings`);
    }

    public createTicketEscalationSettings(settings: TicketEscalationSettings): Promise<TicketEscalationSettings> {
        return this.ticketClient.doPost(`/ticket-escalation-settings`, settings);
    }

    public editTicketEscalationSettings(settings: TicketEscalationSettings): Promise<TicketEscalationSettings> {
        return this.ticketClient.doPut(`/ticket-escalation-settings`, settings);
    }

    // Maps new ticket top level fields to their old workflowData structures
    private mapWorkflowData(ticket) {
        if (ticket.resolutionKey) {
            ticket.resolution = {
                key: ticket.resolutionKey,
                detail: ticket.resolutionDetail,
            };
        }
        if (ticket.alertIds) {
            ticket.alerts = ticket.alertIds;
        }
        return ticket;
    }

    // Convert old style of query in TicketSearch to the new style.
    // Modifies and returns its argument.
    private transformSearchQueriesFormat(ticket) {
        ticket.searches.forEach(search => {
            if (_.isArray(search.query)) {
                search.query = _.object(search.query);
                search.query.queryString = search.query.query;
                delete search.query.query;
            }
        });
        return ticket;
    }
}
